// ============================================================================
// HAI Reporting Tool - Ward Health Card Component
// Displays ward-specific metrics and allows drill-down
// ============================================================================

import { useEffect, useState } from 'react';
import { collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { formatRate, aggregateHCAISurveillanceCases } from '@/utils/calculations';
import type { Ward, DeviceSnapshot, HAIRecord, HCAISurveillanceCase } from '@/types';

interface WardMetrics {
  wardId: string;
  wardName: string;
  status: 'good' | 'caution' | 'alert';
  lastUpdate: string;
  dataComplete: boolean;
  utiRate: number;
  cabsiRate: number;
  vapRate: number;
  activePatients: number;
  atRiskPatients: number;
  totalDeviceDays: number;
  hcaiCases: number;
}

interface WardHealthCardsProps {
  wards?: Ward[];
}

const normalizeWardName = (name?: string | null) => name?.trim().toLowerCase() || '';

/**
 * Component displaying individual ward health cards
 */
export function WardHealthCards({ wards = [] }: WardHealthCardsProps) {
  const [wardMetrics, setWardMetrics] = useState<WardMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedWard, setExpandedWard] = useState<string | null>(null);

  useEffect(() => {
    loadWardMetrics();
  }, [wards]);

  const loadWardMetrics = async () => {
    setLoading(true);
    setError(null);

    try {
      if (wards.length === 0) {
        // Load wards if not provided
        const wardDocs = await getDocs(collection(db, 'wards'));
        const loadedWards = wardDocs.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as Ward))
          .filter(w => w.isActive);

        loadMetricsForWards(loadedWards);
      } else {
        loadMetricsForWards(wards.filter(w => w.isActive));
      }
    } catch (err) {
      console.error('Error loading ward metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load ward metrics');
      setLoading(false);
    }
  };

  const loadMetricsForWards = async (activeWards: Ward[]) => {
    try {
      // Calculate date range (past 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];
      const todayStr = new Date().toISOString().split('T')[0];
      const startTimestamp = Timestamp.fromDate(sevenDaysAgo);
      const endTimestamp = Timestamp.fromDate(new Date());

      const metrics: WardMetrics[] = [];

      // Preload HCAI surveillance cases for the interval and group by ward name
      const hcaiQuery = query(
        collection(db, 'hcaiCases'),
        where('createdAt', '>=', startTimestamp),
        where('createdAt', '<=', endTimestamp),
        orderBy('createdAt', 'asc')
      ) as any;

      const hcaiDocs = await getDocs(hcaiQuery);
      const surveillanceByWard = new Map<string, HCAISurveillanceCase[]>();

      hcaiDocs.docs.forEach(doc => {
        const data = doc.data() as any;
        const normalizedWard = normalizeWardName(data.ward);
        if (!normalizedWard) return;

        const caseRecord: HCAISurveillanceCase = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };

        if (!surveillanceByWard.has(normalizedWard)) {
          surveillanceByWard.set(normalizedWard, []);
        }
        surveillanceByWard.get(normalizedWard)!.push(caseRecord);
      });

      for (const ward of activeWards) {
        // Fetch device snapshots for this ward
        const deviceQuery = query(
          collection(db, 'deviceSnapshots'),
          where('wardId', '==', ward.id),
          where('date', '>=', sevenDaysAgoStr),
          where('date', '<=', todayStr)
        ) as any;

        // Fetch HAI records for this ward
        const haiQuery = query(
          collection(db, 'haiRecords'),
          where('wardId', '==', ward.id),
          where('periodEnd', '>=', sevenDaysAgoStr),
          where('periodEnd', '<=', todayStr)
        ) as any;

        const [deviceDocs, haiDocs] = await Promise.all([
          getDocs(deviceQuery),
          getDocs(haiQuery)
        ]);

        const devices = deviceDocs.docs.map(doc => doc.data()) as DeviceSnapshot[];
        const records = haiDocs.docs.map(doc => doc.data()) as HAIRecord[];
        const wardSurveillanceCases = surveillanceByWard.get(normalizeWardName(ward.name)) || [];
        const aggregatedSurveillance = aggregateHCAISurveillanceCases(wardSurveillanceCases);

        // Calculate metrics
        const totalCVPDays = devices.reduce((sum, d) => sum + (d.patientsWithDevice?.cvp || 0), 0);
        const totalCatheterDays = devices.reduce((sum, d) => sum + (d.patientsWithDevice?.urinaryCatheter || 0), 0);
        const totalVentilatorDays = devices.reduce((sum, d) => sum + (d.patientsWithDevice?.ventilator || 0), 0);
        const totalDeviceDays = totalCVPDays + totalCatheterDays + totalVentilatorDays;

        const totalCABSI = records.reduce((sum, r) => sum + (r.cases?.cabsi || 0), 0) + aggregatedSurveillance.cabsi;
        const totalUTI = records.reduce((sum, r) => sum + (r.cases?.uti || 0), 0) + aggregatedSurveillance.uti;
        const totalVAP = records.reduce((sum, r) => sum + (r.cases?.vap || 0), 0) + aggregatedSurveillance.vap;
        const totalHCAI = totalCABSI + totalUTI + totalVAP;

        const utiRate = totalCatheterDays > 0 ? (totalUTI / totalCatheterDays) * 1000 : 0;
        const cabsiRate = totalCVPDays > 0 ? (totalCABSI / totalCVPDays) * 1000 : 0;
        const vapRate = totalVentilatorDays > 0 ? (totalVAP / totalVentilatorDays) * 1000 : 0;

        // Determine status
        const thresholds = { uti: 2.5, cabsi: 1.5, vap: 2.0 };
        const isAlert = utiRate > thresholds.uti || cabsiRate > thresholds.cabsi || vapRate > thresholds.vap;
        const isCaution = utiRate > thresholds.uti * 0.7 || cabsiRate > thresholds.cabsi * 0.7 || vapRate > thresholds.vap * 0.7;

        // Get last update date
        const lastDeviceUpdate = devices.length > 0
          ? new Date(devices[devices.length - 1].date).toLocaleDateString('en-ZA')
          : 'No data';

        const activePatients = new Set(
          devices.map(d => `${d.wardId}-${d.date}`)
        ).size;

        metrics.push({
          wardId: ward.id,
          wardName: ward.name,
          status: isAlert ? 'alert' : isCaution ? 'caution' : 'good',
          lastUpdate: lastDeviceUpdate,
          dataComplete: devices.length > 0,
          utiRate: Number(utiRate.toFixed(2)),
          cabsiRate: Number(cabsiRate.toFixed(2)),
          vapRate: Number(vapRate.toFixed(2)),
          activePatients,
          atRiskPatients: Math.ceil(activePatients * 0.15),
          totalDeviceDays,
          hcaiCases: totalHCAI
        });
      }

      // Sort by status (alert first)
      metrics.sort((a, b) => {
        const statusOrder = { alert: 0, caution: 1, good: 2 };
        return statusOrder[a.status] - statusOrder[b.status];
      });

      setWardMetrics(metrics);
    } catch (err) {
      console.error('Error loading ward metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load ward metrics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="card h-32 bg-gray-100 animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-danger-50 border border-danger-200">
        <p className="text-sm text-danger-700">{error}</p>
      </div>
    );
  }

  if (wardMetrics.length === 0) {
    return (
      <div className="card text-center py-8">
        <p className="text-gray-600">No ward data available</p>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'alert':
        return 'bg-danger-50 border-danger-200';
      case 'caution':
        return 'bg-warning-50 border-warning-200';
      default:
        return 'bg-success-50 border-success-200';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'alert':
        return '🚨 Alert';
      case 'caution':
        return '⚠️ Caution';
      default:
        return '✅ Good';
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Ward Status Overview</h3>

      {wardMetrics.map(ward => (
        <div
          key={ward.wardId}
          className={`card border ${getStatusColor(ward.status)} cursor-pointer transition-all hover:shadow-md`}
          onClick={() => setExpandedWard(expandedWard === ward.wardId ? null : ward.wardId)}
        >
          {/* Collapsed View */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                {ward.wardName}
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-white">
                  {getStatusBadge(ward.status)}
                </span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                <div>
                  <p className="text-gray-600 text-xs">CAUTI Rate</p>
                  <p className={`font-semibold ${ward.utiRate > 2.5 ? 'text-danger-600' : 'text-gray-900'}`}>
                    {formatRate(ward.utiRate)}/1000
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">CLABSI Rate</p>
                  <p className={`font-semibold ${ward.cabsiRate > 1.5 ? 'text-danger-600' : 'text-gray-900'}`}>
                    {formatRate(ward.cabsiRate)}/1000
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">VAP Rate</p>
                  <p className={`font-semibold ${ward.vapRate > 2.0 ? 'text-danger-600' : 'text-gray-900'}`}>
                    {formatRate(ward.vapRate)}/1000
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">Last Update</p>
                  <p className="font-semibold text-gray-900">{ward.lastUpdate}</p>
                </div>
              </div>
            </div>
            <div className="text-2xl ml-4">
              {expandedWard === ward.wardId ? '▼' : '▶'}
            </div>
          </div>

          {/* Expanded View */}
          {expandedWard === ward.wardId && (
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-xs text-gray-600 mb-1">Active Patients</p>
                  <p className="text-2xl font-bold text-primary-600">{ward.activePatients}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-xs text-gray-600 mb-1">At-Risk Patients</p>
                  <p className="text-2xl font-bold text-danger-600">{ward.atRiskPatients}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-xs text-gray-600 mb-1">Total Device Days</p>
                  <p className="text-2xl font-bold text-info-600">{ward.totalDeviceDays}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-xs text-gray-600 mb-1">HCAI Cases (7d)</p>
                  <p className="text-2xl font-bold text-warning-600">{ward.hcaiCases}</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <p className="text-xs font-semibold text-gray-900 mb-2">Recommendations</p>
                {ward.utiRate > 2.5 && (
                  <p className="text-xs text-danger-700 mb-1">• 🚨 CAUTI rate above target - Review catheter management protocols</p>
                )}
                {ward.cabsiRate > 1.5 && (
                  <p className="text-xs text-danger-700 mb-1">• 🚨 CLABSI rate above target - Review central line care practices</p>
                )}
                {ward.vapRate > 2.0 && (
                  <p className="text-xs text-danger-700 mb-1">• 🚨 VAP rate above target - Review ventilator management</p>
                )}
                {ward.status === 'good' && (
                  <p className="text-xs text-success-700">• Ward performing well - Maintain current practices</p>
                )}
              </div>

              <div className="flex gap-2">
                <button className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                  View Detailed Report →
                </button>
                <button className="text-xs text-gray-600 hover:text-gray-700 font-medium ml-auto">
                  Send Alert
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
